import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRightIcon, ArrowUpRightIcon } from "lucide-react";
import { currentWork, type CurrentWorkItem } from "../data/currentWork";
import { SectionHeading } from "./SectionHeading";
import { EYEBROW, H3 } from "../utils/typography";
const ease = [0.23, 1, 0.32, 1] as const;
export function CurrentlyWorkingOn() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, {
    margin: "-10% 0px"
  });
  const animate = !reduce && inView;
  return <section ref={ref} id="now" aria-labelledby="now-heading" className="w-full scroll-mt-28 bg-cream px-6 pb-24 md:scroll-mt-36 md:px-12 md:pb-32 lg:px-20 lg:pb-40" data-id="element-223" data-mp="2srq61tqhidyj">
      <SectionHeading id="now-heading" title="IN PROGRESS" data-id="element-224" data-mp="1hckq3o1ceiwz" />

      <p className={`mt-24 md:mt-32 lg:mt-40 ${EYEBROW} flex items-center gap-3 text-rust`} data-id="element-225" data-mp="1hazxw52i3c2a">
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true" data-id="element-226" data-mp="12sm0gdo397of">
          {!reduce && <motion.span className="absolute inline-flex h-full w-full rounded-full bg-mustard" animate={{
          scale: [1, 1.8],
          opacity: [0.35, 0]
        }} transition={{
          duration: 2.4,
          ease: "easeOut",
          repeat: Infinity,
          repeatDelay: 1.2
        }} data-id="element-227" data-mp="17qnhyu5v8yxs" />}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-mustard" data-id="element-228" data-mp="k0t9eoskxwjc" />
        </span>
        Currently working on
      </p>

      <ul className="mt-6 grid grid-cols-1 gap-6 md:mt-8 md:grid-cols-2" data-id="element-229" data-mp="a3f2pgwgkm48">
        {currentWork.map((item, i) => <motion.li key={item.id} className={`flex ${item.featured ? "md:col-span-2" : ""}`} initial={reduce ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.25
      }} transition={{
        duration: 0.3,
        ease,
        delay: item.featured ? 0 : (i + 1) % 2 * 0.06
      }} data-id="element-230" data-mp="f28en4w3k52a" data-mpi={i}>
            <WorkItem item={item} animate={animate} offset={i * 0.9} data-id="element-231" data-mp="23095wh44hsme" />
          </motion.li>)}
      </ul>

      <a href="#contact" className="group mt-8 inline-flex min-h-12 items-center gap-3 rounded-full bg-dusty py-2 pl-6 pr-2 text-sm font-medium uppercase tracking-wide text-cream transition-colors duration-150 ease-out hover:bg-espresso focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dusty focus-visible:ring-offset-4 focus-visible:ring-offset-cream" data-id="element-232" data-mp="1y0o8wc8ijucb">
        Curious about any of these? Ask me
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream text-dusty transition-colors duration-150 ease-out group-hover:text-espresso" data-id="element-233" data-mp="2hnlwik8seojn">
          <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-234" data-mp="16b1lxdj5jrkl" />
        </span>
      </a>
    </section>;
}
function WorkItem({
  item,
  animate,
  offset
}: {
  item: CurrentWorkItem;
  animate: boolean;
  offset: number;
}) {
  const linked = Boolean(item.href);
  const card = <article className={`flex h-full w-full flex-col rounded-3xl bg-paper p-6 md:p-8 ${linked ? "transition-colors duration-150 ease-out group-hover:bg-[#e9e0cf]" : ""}`} data-id="element-235" data-mp="3lz72n2tsoscs">
      <div className="flex items-start justify-between gap-4" data-id="element-236" data-mp="3vp9wf58qqac6">
        <p className={`${EYEBROW} text-espresso/70`} data-id="element-237" data-mp="3vquq6sn8dp8z">
          {item.org}
          {item.role && <>
              {" "}
              <span aria-hidden="true" data-id="element-238" data-mp="9h0n0if82h7x">·</span> {item.role}
            </>}
        </p>
        {linked && <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rust text-cream transition-colors duration-150 ease-out group-hover:bg-espresso" data-id="element-239" data-mp="di40iy9ss15z">
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-240" data-mp="2fas3r7mfyolr" />
          </span>}
      </div>
      <h3 className={`mt-3 ${H3} text-espresso`} data-id="element-241" data-mp="1fy75wrcco8wa">{item.title}</h3>
      <p className={`mt-3 text-base leading-relaxed text-espresso/80 ${item.featured ? "max-w-2xl" : "max-w-md"}`} data-id="element-242" data-mp="1tyrobmecs2p9">
        {item.summary}
      </p>

      <div className="mt-auto pt-8" data-id="element-243" data-mp="1oxby7fxbj600">
        <p className={`${EYEBROW} text-espresso/70`} data-id="element-244" data-mp="119aymwi4azwq">{item.label}</p>
        <div className="mt-3" data-id="element-245" data-mp="117hnp50h1tix">
          {item.visual === "questions" && <RotatingQuestion items={item.items} animate={animate} offset={offset} data-id="element-246" data-mp="wcc4wf2d8uz2" />}
          {item.visual === "cycle" && <SprintCycle items={item.items} animate={animate} offset={offset} data-id="element-247" data-mp="1b0yiab8fncnn" />}
          {item.visual === "flow" && <DataFlow items={item.items} animate={animate} data-id="element-248" data-mp="166uajprcf0li" />}
          {item.visual === "assemble" && <Assemble items={item.items} animate={animate} offset={offset} data-id="element-249" data-mp="ihi4uzv85any" />}
          {item.visual === "shifts" && <Shifts items={item.items} data-id="element-250" data-mp="djzvj8daotff" />}
          {item.visual === "report" && <QuickReport question={item.items[0]} animate={animate} offset={offset} data-id="element-251" data-mp="2p6k1bhuyrq02" />}
        </div>
      </div>
      {linked && <span className="sr-only" data-id="element-252" data-mp="2tzwij6xbcuis">Read the case study</span>}
    </article>;
  if (!item.href) return card;
  return <Link to={item.href} className="group flex w-full rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-4 focus-visible:ring-offset-cream" data-id="element-253" data-mp="2g53svfnplxyh">
      {card}
    </Link>;
}

/** Cycles index every `interval` ms while animating; starts after `offset` seconds. */
function useCycle(length: number, animate: boolean, offset: number, interval: number) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let timer: number | undefined;
    const start = window.setTimeout(() => {
      timer = window.setInterval(() => setIndex(i => (i + 1) % length), interval);
    }, offset * 1000);
    return () => {
      window.clearTimeout(start);
      if (timer) window.clearInterval(timer);
    };
  }, [animate, length, offset, interval]);
  return index;
}
function RotatingQuestion({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 5200);
  return <div className="rounded-2xl bg-espresso px-5 py-4" data-id="element-254" data-mp="3ge26dv482ctd">
      {/* Screen readers get the full list once, not a moving target */}
      <ul className="sr-only" data-id="element-255" data-mp="3bhbrvc82fpa2">
        {items.map(q => <li key={q} data-id="element-256" data-mp="36gnyijwd6wco">{q}</li>)}
      </ul>
      <div aria-hidden="true" className="relative h-14 overflow-hidden" data-id="element-257" data-mp="36j0dco6tsqli">
        <AnimatePresence initial={false} mode="popLayout" data-id="element-258" data-mp="31icq8dlpwtwk">
          <motion.p key={items[index]} className="absolute inset-x-0 top-0 text-base font-medium leading-snug text-cream" initial={{
          y: 8,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -8,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease
        }} data-id="element-259" data-mp="2xgr564z159ba">
            {items[index]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>;
}
function SprintCycle({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 3000);
  const layoutId = useId();
  return <ol className="grid grid-cols-2 gap-2" data-id="element-260" data-mp="2rde4dvvrxk7n">
      {items.map((step, i) => {
      const active = animate && i === index;
      return <li key={step} className="relative" data-id="element-261" data-mp="1zmget0opefqe" data-mpi={i}>
            {active && <motion.span layoutId={layoutId} aria-hidden="true" className="absolute inset-0 rounded-full bg-espresso" transition={{
          duration: 0.25,
          ease
        }} data-id="element-262" data-mp="1zokuefvyt36i" />}
            <span className={`relative flex h-10 items-center justify-center rounded-full text-sm font-medium uppercase tracking-wide ring-1 transition-colors duration-200 ease-out ${active ? "text-cream ring-transparent" : "text-espresso ring-espresso/20"}`} data-id="element-263" data-mp="3f2gn77259j3q">
              {step}
            </span>
          </li>;
    })}
    </ol>;
}

/** Reusable sections settle into place one by one, like a page being composed from existing parts. */
function Assemble({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  // Headline, image, copy carry from the printed piece onto the screen, then a beat with all in place
  const step = useCycle(4, animate, offset, 1800);
  const shown = (i: number) => !animate || i < step ? "opacity-100" : "opacity-20";
  const fade = "transition-opacity duration-300 ease-out";
  const [printLabel, webLabel] = items;
  return <div className="flex items-end gap-3" data-id="element-264" data-mp="1t369mlh0i9nu">
      <p className="sr-only" data-id="element-265" data-mp="1t3xr6sdry6v4">The printed campaign pieces carried onto the website.</p>

      {/* Printed piece: portrait sheet, always complete */}
      <figure aria-hidden="true" className="shrink-0" data-id="element-266" data-mp="1exmdx5lhiwit">
        <div className="flex h-[5.5rem] w-16 flex-col gap-1.5 rounded-md bg-cream p-2 shadow-[0_6px_14px_-8px_rgba(29,24,20,0.35)] ring-1 ring-espresso/10" data-id="element-267" data-mp="1eyxk1c2ekgd3">
          <span className="h-1.5 w-3/4 rounded-full bg-mustard" data-id="element-268" data-mp="1582r5ouniiio" />
          <span className="h-7 rounded-sm bg-espresso/80" data-id="element-269" data-mp="155qd3ms0ksaw" />
          <span className="h-1 w-full rounded-full bg-espresso/25" data-id="element-270" data-mp="1a5vzoojffom4" />
          <span className="h-1 w-2/3 rounded-full bg-espresso/25" data-id="element-271" data-mp="v9wr7csenmgg" />
        </div>
        <figcaption className="mt-2 text-xs font-medium uppercase tracking-wide text-espresso/60" data-id="element-272" data-mp="vbzs4rv3izzy">{printLabel}</figcaption>
      </figure>

      <ArrowRightIcon aria-hidden="true" className="mb-12 h-4 w-4 shrink-0 text-espresso/35" data-id="element-273" data-mp="m9qlwe6v1ssd" />

      {/* Screen: the same pieces settle in one at a time */}
      <figure aria-hidden="true" className="min-w-0 flex-1" data-id="element-274" data-mp="r9wepxov9lc1">
        <div className="flex h-[5.5rem] flex-col overflow-hidden rounded-lg bg-cream shadow-[0_6px_14px_-8px_rgba(29,24,20,0.35)] ring-1 ring-espresso/10" data-id="element-275" data-mp="2goax809akbz3">
          <div className="flex gap-1 border-b border-espresso/10 px-2 py-1.5" data-id="element-276" data-mp="efyqis8dpvoh">
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-277" data-mp="edvpze9lok5i" />
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-278" data-mp="j7q7qc5nap1y" />
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-279" data-mp="5g1xyfivcco5" />
          </div>
          <div className="grid flex-1 grid-cols-[minmax(0,1fr)_40%] gap-2 p-2" data-id="element-280" data-mp="9fu98zzncwx7">
            <div className="flex flex-col gap-1.5" data-id="element-281" data-mp="x6g57swdmqqu">
              <span className={`h-1.5 w-3/4 rounded-full bg-mustard ${fade} ${shown(0)}`} data-id="element-282" data-mp="x6pvzip8x1xl" />
              <span className={`flex flex-col gap-1.5 pt-1 ${fade} ${shown(2)}`} data-id="element-283" data-mp="124reo2irez8j">
                <span className="h-1 w-full rounded-full bg-espresso/25" data-id="element-284" data-mp="o4go70ld3mnw" />
                <span className="h-1 w-5/6 rounded-full bg-espresso/25" data-id="element-285" data-mp="t43cc52qanbh" />
                <span className="h-1 w-2/3 rounded-full bg-espresso/25" data-id="element-286" data-mp="t6ftig0nwtnh" />
              </span>
            </div>
            <span className={`rounded-sm bg-espresso/80 ${fade} ${shown(1)}`} data-id="element-287" data-mp="3duu28mkg6vlw" />
          </div>
        </div>
        <figcaption className="mt-2 text-xs font-medium uppercase tracking-wide text-espresso/60" data-id="element-288" data-mp="35djntrxpd6rw">{webLabel}</figcaption>
      </figure>
    </div>;
}

/** Static from → to pairs; the outcome word carries the accent. Deliberately still beside the animated tiles. */
function Shifts({
  items
}: {
  items: string[];
}) {
  return <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2" data-id="element-289" data-mp="3adxylhqouutm">
      {items.map(pair => {
      const [from, to] = pair.split("→").map(s => s.trim());
      return <li key={pair} className="flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-espresso px-4 text-xs font-medium uppercase tracking-wide text-cream/70 sm:text-sm" data-id="element-290" data-mp="2hpjxuu87tked">
            {from}
            <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 text-cream/40" aria-label="to" data-id="element-291" data-mp="23wu94i1ay1dk" />
            <span className="text-mustard" data-id="element-292" data-mp="23uhmvsoih4up">{to}</span>
          </li>;
    })}
    </ul>;
}
const REPORT_BARS = [38, 54, 46, 72, 64, 88];

/** A plain-language question, answered by a small chart whose bars rise softly, hold, then reset. */
function QuickReport({
  question,
  animate,
  offset
}: {
  question: string;
  animate: boolean;
  offset: number;
}) {
  // Step 0 = asked, bars low; step 1 = answered, bars at full height
  const step = useCycle(2, animate, offset, 3800);
  const answered = !animate || step === 1;
  return <div className="flex h-[5.5rem] items-end gap-4 rounded-2xl bg-espresso px-4 py-3" data-id="element-293" data-mp="3g1wkx08rm98d">
      <p className="min-w-0 flex-1 self-center text-sm font-medium leading-snug text-cream" data-id="element-294" data-mp="3kzpnzkerun4f">
        <span className="text-mustard" data-id="element-295" data-mp="3p1b89qtmy3on">“</span>
        {question}
        <span className="text-mustard" data-id="element-296" data-mp="3p3xbje9htx12">”</span>
      </p>
      <div aria-hidden="true" className="flex h-full shrink-0 items-end gap-1" data-id="element-297" data-mp="3u1yv00atzyd2">
        {REPORT_BARS.map((h, i) => <span key={i} className={`w-2 rounded-sm transition-[height] duration-300 ease-out ${i === REPORT_BARS.length - 1 ? "bg-mustard" : "bg-cream/35"}`} style={{
        height: answered ? `${h}%` : "12%",
        transitionDelay: answered ? `${i * 50}ms` : "0ms"
      }} data-id="element-298" data-mp="14b28rpbwdfe8" data-mpi={i} />)}
      </div>
    </div>;
}

/** Three nodes joined by tracks; a mustard pulse travels from the first node through the middle to the last. */
function DataFlow({
  items,
  animate
}: {
  items: string[];
  animate: boolean;
}) {
  return <div className="flex h-[5.5rem] items-center rounded-2xl bg-espresso px-4" data-id="element-299" data-mp="10p5i8ks4vput">
      <p className="sr-only" data-id="element-300" data-mp="vohty6vahn4a">{items.join(" connects through ")}</p>
      <div aria-hidden="true" className="flex w-full items-center" data-id="element-301" data-mp="vrdrhu3869s7">
        {items.map((node, i) => <React.Fragment key={node} data-id="element-302" data-mp="qrrbxdzxchg8" data-mpi={i}>
            <span className={`flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-3 text-sm sm:px-4 font-medium uppercase tracking-wide ${i === 1 ? "bg-mustard text-espresso" : "text-cream ring-1 ring-cream/25"}`} data-id="element-303" data-mp="mp4k3d396wj1">
              {node}
            </span>
            {i < items.length - 1 && <span className="relative mx-2 h-1.5 flex-1 overflow-hidden" data-id="element-304" data-mp="2t56qs7ifqmkk">
                <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-cream/20" data-id="element-305" data-mp="2t2aq4bf7djsf" />
                {animate && <motion.span className="absolute top-1/2 h-1 w-4 -translate-y-1/2 rounded-full bg-mustard/70" initial={{
            left: "-25%"
          }} animate={{
            left: "110%"
          }} transition={{
            duration: 1.6,
            ease: "linear",
            repeat: Infinity,
            repeatDelay: 2.4,
            delay: i * 1.6
          }} data-id="element-306" data-mp="2j5xx7frhf9yq" />}
              </span>}
          </React.Fragment>)}
      </div>
    </div>;
}